import { analysisApi } from '@/features/analysisApi';
import { createApi } from '@reduxjs/toolkit/query/react';
import type {
  CustomListMetadata,
  CustomListBulkAddRequest,
  AggregateResponseListSpanCustomListSummary,
  CustomListCreate,
  CustomListSummary,
  CustomListUpdate,
  GroupAnalysisType,
  GroupAnalysisFilter,
} from '@/generated/types/navigator/types.gen';
import { baseQueryWithAuth } from './utils';
import snakeCase from 'lodash/snakeCase';
import type {
  BoundingBoxString,
  SavedCustomList,
  SavedListResponse,
} from '@/features/types';
import { encodeFilterQuery } from './filterHelpers';

type PaginationParams = {
  page?: number;
  sortBy?: 'updated_at' | 'created_at' | string;
  sortOrder?: 'asc' | 'desc';
};

type BboxMetadata = {
  bbox: BoundingBoxString;
} & CustomListMetadata;

const isSavedList = (list: CustomListSummary): list is SavedCustomList =>
  Boolean(list.id);

const toSavedListResponse = ({
  data,
  summary,
}: AggregateResponseListSpanCustomListSummary): SavedListResponse => {
  // Ensure the summary has a non-null id before proceeding
  if (!summary || !isSavedList(summary)) {
    throw new Error('The list response has no id');
  }

  return { data: data ?? [], summary };
};

type SearchParams = PaginationParams & {
  analysisType?: GroupAnalysisType;
  encoded_filter_query?: string;
};

function generateParams(params: SearchParams) {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      searchParams.append(snakeCase(key), String(value));
    }
  }

  return searchParams.toString();
}

const baseRoute = '/v1/list';
export const listApi = createApi({
  reducerPath: 'listApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Lists'],
  endpoints: (build) => ({
    createList: build.mutation<SavedListResponse, CustomListCreate>({
      query: (body) => ({
        url: `${baseRoute}/`,
        method: 'POST',
        body,
      }),
      transformResponse: toSavedListResponse,
      invalidatesTags: [{ type: 'Lists', id: 'LIST' }],
    }),
    getLists: build.query<SavedCustomList[], PaginationParams | void>({
      query: (params) => {
        const queryString = params ? `?${generateParams(params)}` : '';
        return `${baseRoute}/${queryString}`;
      },
      transformResponse: (lists: CustomListSummary[]) =>
        lists.filter(isSavedList),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Lists' as const, id })),
              { type: 'Lists', id: 'LIST' },
            ]
          : [{ type: 'Lists', id: 'LIST' }],
    }),
    getListById: build.query<
      SavedListResponse,
      {
        params?: PaginationParams & {
          analysisType?: GroupAnalysisType;
          filterQuery?: GroupAnalysisFilter;
        };
        id: string;
      }
    >({
      query: ({ id, params }) => {
        const { filterQuery, ...restParams } = params || {};
        const sentParams: SearchParams = { ...restParams };
        if (filterQuery) {
          sentParams.encoded_filter_query = encodeFilterQuery(filterQuery);
        }

        return {
          url: `${baseRoute}/${id}?${generateParams(sentParams)}`,
        };
      },
      transformResponse: toSavedListResponse,
      providesTags: (result, _error, { id }) =>
        result ? [{ type: 'Lists', id }] : [],
    }),
    addSpanToList: build.mutation<
      SavedListResponse,
      { listId: string; level2: string; level3: string }
    >({
      query: ({ listId, level2, level3 }) => ({
        url: `${baseRoute}/${listId}/${level2}/${level3}`,
        method: 'PUT',
      }),
      transformResponse: toSavedListResponse,
      invalidatesTags: (_result, _err, { listId }) => [
        { type: 'Lists', id: listId },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        // Should invalidate so the span popup updates
        dispatch(
          analysisApi.util.invalidateTags([
            { type: 'groupItemData', id: arg.level3 },
            { type: 'analysisData' },
          ])
        );
      },
    }),
    bulkRemoveFromList: build.mutation<
      SavedListResponse,
      { listId: string; body: { removal_filter: string } }
    >({
      query: ({ listId, body }) => ({
        url: `${baseRoute}/${listId}/bulk_remove`,
        method: 'POST',
        body,
      }),
      transformResponse: toSavedListResponse,
      invalidatesTags: (_result, _err, { listId }) => [
        { type: 'Lists', id: listId },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(
          analysisApi.util.invalidateTags([
            { type: 'analysisData' },
            { type: 'itemData' },
          ])
        );
      },
    }),
    getListBbox: build.query<BboxMetadata, { listId: string }>({
      query: ({ listId }) => ({
        url: `${baseRoute}/${listId}/bbox`,
        method: 'GET',
      }),
      providesTags: (result, _error, { listId }) =>
        result ? [{ type: 'Lists', id: listId }] : [],
    }),
    removeSpanFromList: build.mutation<
      SavedListResponse,
      { listId: string; level2: string; level3: string }
    >({
      query: ({ listId, level2, level3 }) => ({
        url: `${baseRoute}/${listId}/${level2}/${level3}`,
        method: 'DELETE',
      }),
      transformResponse: toSavedListResponse,
      invalidatesTags: (_result, _err, { listId }) => [
        { type: 'Lists', id: listId },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        // Should invalidate so the span popup updates
        dispatch(
          analysisApi.util.invalidateTags([
            { type: 'groupItemData', id: arg.level3 },
            { type: 'analysisData' },
          ])
        );
      },
    }),
    deleteList: build.mutation<void, string>({
      query: (listId) => ({
        url: `${baseRoute}/${listId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _err, listId) => [
        { type: 'Lists', id: listId },
      ],
    }),
    updateList: build.mutation<
      SavedListResponse,
      { listId: string; body: Partial<CustomListUpdate> }
    >({
      query: ({ listId, body }) => ({
        url: `${baseRoute}/${listId}`,
        method: 'PUT',
        body,
      }),
      transformResponse: toSavedListResponse,
      invalidatesTags: (_result, _err, { listId }) => [
        { type: 'Lists', id: listId },
      ],
    }),
    bulkAddSpansToList: build.mutation<
      SavedListResponse,
      { listId: string; body: CustomListBulkAddRequest }
    >({
      query: ({ listId, body }) => ({
        url: `${baseRoute}/${listId}/bulk_add`,
        method: 'POST',
        body,
      }),
      transformResponse: toSavedListResponse,
      invalidatesTags: (_result, _err, { listId }) => [
        { type: 'Lists', id: listId },
      ],
    }),
    uploadCsvToCreateList: build.mutation<
      SavedListResponse,
      {
        listName: string;
        csvFile: File;
        level2Header?: string;
        level3Header?: string;
      }
    >({
      query: ({
        listName,
        csvFile,
        level2Header = 'level2',
        level3Header = 'level3',
      }) => {
        const formData = new FormData();
        formData.append('list_name', listName);
        formData.append('csv_file', csvFile);
        formData.append('level2_header', level2Header);
        formData.append('level3_header', level3Header);

        return {
          url: `${baseRoute}/upload`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: toSavedListResponse,
      invalidatesTags: [{ type: 'Lists', id: 'LIST' }],
    }),
  }),
});

export const resetListApiState = listApi.util.resetApiState;

export const {
  useGetListsQuery,
  useGetListByIdQuery,
  useCreateListMutation,
  useAddSpanToListMutation,
  useBulkAddSpansToListMutation,
  useRemoveSpanFromListMutation,
  useBulkRemoveFromListMutation,
  useDeleteListMutation,
  useUpdateListMutation,
  useGetListBboxQuery,
  useUploadCsvToCreateListMutation,
} = listApi;
